import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const useHeaderStyles = makeStyles({
  header: {
    backgroundColor: tokens.colorBrandBackground,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: '0 16px',
    height: '48px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 480px)': {
      padding: '0 8px',
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    minWidth: 0,
    '@media (max-width: 480px)': {
      gap: '8px',
    },
  },
  title: {
    color: tokens.colorNeutralForegroundOnBrand,
    margin: '0',
    fontSize: '18px',
    fontWeight: '600',
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    '@media (max-width: 480px)': {
      fontSize: '16px',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  supportButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '36px',
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: 'transparent',
    textDecoration: 'none',
    cursor: 'pointer',
    ...shorthands.border('0', 'none', 'transparent'),
    borderRadius: '4px',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      color: 'ButtonText',
      backgroundColor: 'ButtonFace',
      ...shorthands.border('1px', 'solid', 'ButtonText'),
      outline: '1px solid ButtonText',
      outlineOffset: '-1px',
      padding: '0 8px',
      ':hover': {
        color: 'HighlightText',
        backgroundColor: 'Highlight',
        ...shorthands.border('1px', 'solid', 'Highlight'),
        outlineColor: 'Highlight',
      },
      ':focus-visible': {
        outline: '2px solid Highlight',
        outlineOffset: '2px',
      },
    },
  },
  contrastThemeIcon: {
    display: 'inline-flex',
    '@media (forced-colors: active)': {
      display: 'none',
    },
  },
  contrastThemeLabel: {
    display: 'none',
    color: 'inherit',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'normal',
    '@media (forced-colors: active)': {
      display: 'inline',
    },
  },
  supportPanelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 24px',
    flex: 1,
    overflowY: 'auto',
  },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  supportInstructionRow: {
    display: 'block',
  },
  supportInstruction: {
    margin: 0,
  },
  caseIdAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  inlineLink: {
    color: '#115EA3',
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    textDecorationThickness: '1px',
    ':hover': {
      color: tokens.colorBrandForegroundLinkHover,
      textDecorationLine: 'underline',
      textDecorationThickness: '2px',
    },
    ':focus-visible': {
      textDecorationLine: 'underline',
      textDecorationThickness: '2px',
    },
    ':active': {
      textDecorationLine: 'underline',
    },
    ':visited': {
      textDecorationLine: 'underline',
    },
    '@media (forced-colors: active)': {
      color: 'LinkText',
      textDecorationLine: 'underline',
    },
  },
  copyStatus: {
    display: 'inline-block',
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  supportPanelText: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.5',
    margin: 0,
  },
  supportPanelImage: {
    width: '200px',
    height: 'auto',
    display: 'block',
    margin: 'auto',
  },
  supportPanelFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '16px 24px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  moreButton: {
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: 'transparent',
    border: 'none',
    minWidth: 'auto',
    padding: '8px',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  askHRButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '36px',
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: 'transparent',
    ...shorthands.border('0', 'none', 'transparent'),
    borderRadius: '4px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      color: 'ButtonText',
      backgroundColor: 'ButtonFace',
      ...shorthands.border('1px', 'solid', 'ButtonText'),
      outline: '1px solid ButtonText',
      outlineOffset: '-1px',
      padding: '0 8px',
      ':hover': {
        color: 'HighlightText',
        backgroundColor: 'Highlight',
        ...shorthands.border('1px', 'solid', 'Highlight'),
        outlineColor: 'Highlight',
      },
      ':focus-visible': {
        outline: '2px solid Highlight',
        outlineOffset: '2px',
      },
    },
  },
  avatar: {
    cursor: 'pointer',
    border: `2px solid ${tokens.colorNeutralForegroundOnBrand}`,
    marginRight: '0',
    marginLeft: '6px'
  },
});
